<script lang="ts" setup>
import { ref, watch } from 'vue';
import Mdi from './Mdi.vue';

const props = defineProps<{
  modelValue: string;
  options: string[];
  placeholder: string;
  label?: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const isOpen = ref(false);
const highlightedOptionIndex = ref(0);

/**
 * 候補一覧を開く
 */
const openOptions = (): void => {
  if (props.disabled) {
    return;
  }
  isOpen.value = true;
  highlightedOptionIndex.value = 0;
};

/**
 * 候補一覧を閉じる
 */
const closeOptions = (): void => {
  isOpen.value = false;
  highlightedOptionIndex.value = -1;
};

/**
 * 候補一覧を閉じる(遅延)
 */
const closeOptionsDelayed = (): void => {
  window.setTimeout(() => {
    closeOptions();
  }, 120);
};

/**
 * 候補値を選択する
 * @param optionValue - 選択した候補値
 */
const selectOption = (optionValue: string): void => {
  emit('update:modelValue', optionValue);
  closeOptions();
};

/**
 * 入力変更を処理する
 * @param event - 入力イベント
 */
const onInput = (event: Event): void => {
  emit('update:modelValue', (event.target as HTMLInputElement).value);
  openOptions();
};

/**
 * 入力欄のキー入力を処理する
 * @param event - キーボードイベント
 */
const onKeydown = (event: KeyboardEvent): void => {
  if (props.options.length === 0) {
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    if (!isOpen.value) {
      openOptions();
      highlightedOptionIndex.value = 0;
      return;
    }
    highlightedOptionIndex.value = Math.min(highlightedOptionIndex.value + 1, props.options.length - 1);
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    if (!isOpen.value) {
      openOptions();
      highlightedOptionIndex.value = props.options.length - 1;
      return;
    }
    highlightedOptionIndex.value = Math.max(highlightedOptionIndex.value - 1, 0);
    return;
  }

  if (event.key === 'Enter' && isOpen.value) {
    if (highlightedOptionIndex.value >= 0 && highlightedOptionIndex.value < props.options.length) {
      event.preventDefault();
      selectOption(props.options[highlightedOptionIndex.value]);
    }
    return;
  }

  if (event.key === 'Escape') {
    closeOptions();
  }
};

watch(
  () => props.options,
  (nextOptions) => {
    if (nextOptions.length === 0) {
      highlightedOptionIndex.value = -1;
      return;
    }
    highlightedOptionIndex.value = Math.min(Math.max(highlightedOptionIndex.value, 0), nextOptions.length - 1);
  }
);
</script>

<template>
  <div class="suggest-input">
    <label v-if="label != null && label !== ''" class="suggest-input__label">{{ label }}</label>
    <div class="suggest-input__field-wrapper">
      <input
        :value="modelValue"
        class="suggest-input__input"
        type="text"
        :placeholder="placeholder"
        :disabled="disabled"
        @focus="openOptions"
        @blur="closeOptionsDelayed"
        @input="onInput"
        @keydown="onKeydown"
      />
      <button type="button" class="suggest-input__toggle" :disabled="disabled" @mousedown.prevent @click="openOptions">
        <Mdi icon="arrow_drop_down" />
      </button>
      <div v-if="isOpen && options.length > 0" class="suggest-input__options">
        <button
          v-for="(optionValue, optionIndex) in options"
          :key="`suggest-input-option-${optionValue}`"
          type="button"
          class="suggest-input__option"
          :class="{ 'suggest-input__option--active': highlightedOptionIndex === optionIndex }"
          @mousedown.prevent="selectOption(optionValue)"
        >
          {{ optionValue }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.suggest-input {
  display: grid;
  gap: 6px;

  &__label {
    margin: 0;
    font-size: 14px;
    color: $textDefault;
  }

  &__field-wrapper {
    position: relative;
    display: grid;
    align-items: center;
  }

  &__input {
    width: 100%;
    height: 40px;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    padding: 0 40px 0 12px;
    font-size: 14px;
    color: $textDefault;
    background-color: $semanticWhite;
    box-sizing: border-box;

    &:disabled {
      background-color: $surfaceDefault;
      color: $textSecondary;
      cursor: not-allowed;
    }
  }

  &__toggle {
    position: absolute;
    right: 8px;
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    border: none;
    background-color: transparent;
    color: $textSecondary;
    cursor: pointer;
  }

  &__options {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    width: 100%;
    max-height: 220px;
    overflow-y: auto;
    display: grid;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    background-color: $semanticWhite;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    z-index: 20;
  }

  &__option {
    height: 40px;
    padding: 0 12px;
    display: grid;
    align-items: center;
    text-align: left;
    border: none;
    background-color: $semanticWhite;
    color: $textDefault;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: $semanticPrimarySubdued;
    }

    &--active {
      background-color: $semanticPrimarySubdued02;
      font-weight: 700;
    }
  }
}
</style>
